import React, { useEffect, useState } from "react";

import { IconButton } from "veneer/IconButton";
import { CloseIcon } from "veneer/SvgIcon";
import Typography from "veneer/Typography";

import {
  PARAM_TYPE_ARRAY,
  PARAM_TYPE_BOOLEAN,
  PARAM_TYPE_NUMBER,
  PARAM_TYPE_STRING,
} from "cortex/ui/src/constant/application";
import { Schema } from "cortex/ui/src/fleet/view/application/model";
import { List } from "cortex/ui/src/ui/list";

import { BooleanField } from "./BooleanField";
import { NumberField } from "./NumberField";
import Overrides from "./Overrides";
import { ParameterSection } from "./ParameterSection";
import { SelectField } from "./SelectField";
import { StringField } from "./StringField";
import ParamUtils, { Priority } from "./utils";

interface ParamValue {
  name: string;
  value: unknown;
}

interface FormField {
  displayName?: string;
  name: string;
  value: unknown;
}

interface ParamFormFieldProps {
  deviceParam?: ParamValue;
  errors?: string[];
  field: FormField;
  form: {
    errors?: Record<string, unknown>;
    setFieldValue: (name: string, value: unknown) => void;
    submitCount?: number;
  };
  network: {
    id: number;
  };
  networkParams: ParamValue[];
  onOverride?: (value: boolean) => void;
  onRemove?: () => void;
  priority: Priority;
  readOnly?: boolean;
  schema: Schema;
}

interface FieldProps {
  field: FormField;
  handleChange: (value: unknown) => void;
  helperText?: string | null;
  isError: boolean;
  isRequired: boolean;
  readOnly: boolean;
  schema: Schema;
}

function getBooleanField(field: FormField) {
  return {
    ...field,
    value: typeof field.value === "boolean" ? field.value : null,
  };
}

function getNumberField(field: FormField) {
  return {
    ...field,
    value:
      typeof field.value === "number" || typeof field.value === "string"
        ? field.value
        : null,
  };
}

function getSelectField(field: FormField) {
  if (Array.isArray(field.value)) {
    return {
      ...field,
      value: field.value.filter((value): value is string => {
        return typeof value === "string";
      }),
    };
  }

  return {
    ...field,
    value: typeof field.value === "string" ? field.value : null,
  };
}

function getStringField(field: FormField) {
  return {
    ...field,
    value: typeof field.value === "string" ? field.value : null,
  };
}

function inputFieldFactory(dataType: string, fieldProps: FieldProps) {
  const { schema } = fieldProps;
  const commonFieldProps = {
    error: fieldProps.isError,
    helperText: fieldProps.helperText,
    isRequired: fieldProps.isRequired,
    readOnly: fieldProps.readOnly,
  };
  const field = {
    ...fieldProps.field,
    displayName: schema.display_name,
  };

  if (dataType === PARAM_TYPE_ARRAY) {
    return (
      <SelectField
        {...commonFieldProps}
        field={getSelectField(field)}
        handleChange={(value) => fieldProps.handleChange(value)}
        isMulti={true}
        schema={fieldProps.schema}
      />
    );
  }

  if (dataType === PARAM_TYPE_BOOLEAN) {
    return (
      <BooleanField
        {...commonFieldProps}
        field={getBooleanField(field)}
        handleChange={(value) => fieldProps.handleChange(value)}
      />
    );
  }

  if (dataType === PARAM_TYPE_NUMBER) {
    return (
      <NumberField
        {...commonFieldProps}
        field={getNumberField(field)}
        handleChange={(value) => fieldProps.handleChange(value)}
      />
    );
  }

  if (dataType === PARAM_TYPE_STRING) {
    if (schema.options) {
      return (
        <SelectField
          {...commonFieldProps}
          field={getSelectField(field)}
          handleChange={(value) => fieldProps.handleChange(value)}
          isMulti={false}
          schema={fieldProps.schema}
        />
      );
    }

    return (
      <StringField
        {...commonFieldProps}
        field={getStringField(field)}
        handleChange={(value) => fieldProps.handleChange(value)}
      />
    );
  }

  return (
    <StringField
      {...commonFieldProps}
      field={getStringField(field)}
      handleChange={(value) => fieldProps.handleChange(value)}
    />
  );
}

const ParamFormField = ({
  deviceParam,
  errors = [],
  field,
  form,
  network,
  networkParams,
  onOverride = () => {},
  onRemove,
  priority,
  readOnly = false,
  schema,
}: ParamFormFieldProps) => {
  const { name, default_value: defaultValue } = schema;
  const networkParam = ParamUtils.getNetworkLevelParam(name, networkParams);
  const priorities = ParamUtils.buildPriorities(
    defaultValue,
    networkParam,
    deviceParam,
  );
  const [hasOverride] = useState(Overrides.hasOverride(priority, priorities));
  const [isOverriding, setIsOverriding] = useState(() =>
    Overrides.isOverriding(priority, priorities),
  );

  useEffect(() => {
    if (hasOverride) {
      onOverride(isOverriding);
    }
  }, []);

  const { data_type: dataType = "", required: isRequired = false } = schema;
  const handleChange = (value: unknown): void => {
    form.setFieldValue(field.name, value);
  };

  const hasNetworkLevel = Boolean(
    ParamUtils.getNetworkLevelParam(field.name, networkParams),
  );
  const hasDefaultValue = ParamUtils.hasDefaultValue(schema);
  const hasErrors = errors.length > 0;
  const fieldError = form.errors?.[field.name];
  const validationError =
    typeof fieldError === "string" ? fieldError : undefined;
  const fieldHelperText =
    (form.submitCount || 0) > 0 ? (validationError ?? null) : null;
  const hasValidationError = fieldHelperText !== null;

  const removeButton = (
    <IconButton
      color="error"
      dataTestId="Remove.Button"
      disabled={readOnly}
      icon={<CloseIcon size="small" />}
      onClick={onRemove}
      size="small"
      title="Click to unset this parameter."
    />
  );

  const fieldProps = {
    field,
    handleChange,
    helperText: fieldHelperText,
    isError: hasValidationError,
    isRequired,
    schema,
    readOnly: readOnly || (hasOverride && !isOverriding),
  };
  const inputField = inputFieldFactory(dataType, fieldProps);

  const overrideToggle = hasOverride ? (
    <Overrides.Toggle
      defaultValue={isOverriding}
      disabled={readOnly}
      onChange={(value: boolean) => {
        setIsOverriding(value);
        onOverride(value);
      }}
    >
      <Overrides.Label
        hasDefaultValue={hasDefaultValue}
        hasNetworkLevel={hasNetworkLevel}
        networkId={network.id}
        priority={priority}
      />
    </Overrides.Toggle>
  ) : null;

  const helperText = hasErrors ? (
    <Typography component="div" variant="body2">
      <List items={errors} />
    </Typography>
  ) : (
    schema.description && (
      <Typography component="div" variant="body2">
        {schema.description}
      </Typography>
    )
  );

  return (
    <ParameterSection
      action={!isRequired && !hasOverride ? removeButton : null}
      description={helperText}
      input={inputField}
    >
      {overrideToggle}
    </ParameterSection>
  );
};

export { inputFieldFactory, ParamFormField };
